'use client';

import dynamic from 'next/dynamic';
import { useState } from 'react';
import type { MediaDto } from '@/lib/sanity/dto';
import { IconArrowsOut } from '@/components/icons/IconArrowsOut';
import { IconCarProfile } from '@/components/icons/IconCarProfile';
import { IconImagesSquare } from '@/components/icons/IconImagesSquare';
import { VEHICLE_IMAGE_SIZES } from '@/features/vehicle';
import { resolveImageAlt } from '@/lib/sanity/image';
import { usePreloadPhoto } from './usePreloadPhoto';
import { VehicleGalleryArrow } from './VehicleGalleryArrow';
import { VehicleGalleryPhoto } from './VehicleGalleryPhoto';
import { VehicleThumbStrip } from './VehicleThumbStrip';

// The viewer's dialog code loads with the first click on the photo.
const VehicleGalleryViewer = dynamic(
    () => import('./VehicleGalleryViewer').then((module) => module.VehicleGalleryViewer),
    { ssr: false },
);

type VehicleGalleryProps = {
    images: MediaDto[];
    title: string;
    badges: string[];
};

/**
 * Photo, arrows and counter, the thumbnail strip below, the full-screen viewer
 * on a click. The first photo is the page's LCP element.
 */
export function VehicleGallery({ images, title, badges }: VehicleGalleryProps) {
    const [index, setIndex] = useState(0);
    const [loadedIndex, setLoadedIndex] = useState<number>();
    const [viewerOpen, setViewerOpen] = useState(false);
    const [viewerMounted, setViewerMounted] = useState(false);

    // A photo the server has not encoded yet takes it about a second, so the next
    // one goes out together with the shown one, not after it; the previous one
    // follows the shown photo. Both wait for the first photo, the LCP element.
    // The viewer shares the index, so this covers it too.
    usePreloadPhoto(images[index + 1], VEHICLE_IMAGE_SIZES.gallery, loadedIndex !== undefined);
    usePreloadPhoto(images[index - 1], VEHICLE_IMAGE_SIZES.gallery, loadedIndex === index);

    const count = images.length;
    const image = images[index];
    const altFor = (position: number) =>
        resolveImageAlt(images[position], count > 1 ? `${title}, Bild ${position + 1} von ${count}` : title);

    const openViewer = () => {
        setViewerMounted(true);
        setViewerOpen(true);
    };

    if (!image) {
        return (
            <div className="vehicle-gallery">
                <div className="vehicle-media vehicle-media--hero vehicle-media--empty">
                    <IconCarProfile className="vehicle-media__placeholder" aria-hidden="true" />
                    <span className="label-sm">Fotos folgen</span>
                </div>
            </div>
        );
    }

    return (
        <div className="vehicle-gallery">
            <div className="vehicle-media vehicle-media--hero">
                <button type="button" className="vehicle-gallery__open" aria-haspopup="dialog" onClick={openViewer}>
                    <VehicleGalleryPhoto
                        key={index}
                        image={image}
                        alt={altFor(index)}
                        placeholder={loadedIndex !== undefined && loadedIndex !== index}
                        onLoad={() => setLoadedIndex(index)}
                        lcp={index === 0}
                    />
                    <span className="sr-only">Vollansicht öffnen</span>
                </button>

                <div className="vehicle-media__scrim" />
                <span className="vehicle-gallery__expand" aria-hidden="true">
                    <IconArrowsOut width={17} height={17} />
                </span>

                {badges.length ? (
                    <div className="vehicle-media__badges vehicle-gallery__badges">
                        {badges.map((badge) => (
                            <span key={badge} className="vehicle-chip label-xs">
                                {badge}
                            </span>
                        ))}
                    </div>
                ) : null}

                {count > 1 ? (
                    <>
                        <VehicleGalleryArrow direction="prev" atEnd={index === 0} onStep={() => setIndex(index - 1)} />
                        <VehicleGalleryArrow
                            direction="next"
                            atEnd={index === count - 1}
                            onStep={() => setIndex(index + 1)}
                        />
                        <span className="vehicle-chip vehicle-gallery__counter label-xs">
                            <IconImagesSquare width={15} height={15} aria-hidden="true" />
                            {index + 1} / {count}
                        </span>
                    </>
                ) : null}
            </div>

            {count > 1 ? (
                <VehicleThumbStrip images={images} activeIndex={index} onSelect={setIndex} altFor={altFor} />
            ) : null}

            {viewerMounted ? (
                <VehicleGalleryViewer
                    open={viewerOpen}
                    images={images}
                    index={index}
                    onIndex={setIndex}
                    onClose={() => setViewerOpen(false)}
                    altFor={altFor}
                />
            ) : null}
        </div>
    );
}
